import { Injectable, inject, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { interval, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { LoginService } from '../../pages/login/login.service';

@Injectable({
    providedIn: 'root'
})
export class UserStatusService implements OnDestroy {
    private baseUrl = environment.baseUrl;
    private http = inject(HttpClient);
    private loginService = inject(LoginService);
    private statusUpdateSubscription?: Subscription;
    private readonly UPDATE_INTERVAL = 60000; // 1 minute in milliseconds

    /**
     * Starts the periodic online status update
     * Call this after successful login
     */
    startStatusUpdates(): void {
        // Stop any existing updates first
        this.stopStatusUpdates();

        // Only start if user is logged in
        if (!this.loginService.isLoggedIn()) {
            return;
        }

        // Update immediately on start
        this.updateOnlineStatus().subscribe();

        // Then update every 1 minute
        this.statusUpdateSubscription = interval(this.UPDATE_INTERVAL)
            .pipe(
                switchMap(() => {
                    // Check if still logged in before each update
                    if (this.loginService.isLoggedIn()) {
                        return this.updateOnlineStatus();
                    }
                    // If not logged in, stop updates
                    this.stopStatusUpdates();
                    return [];
                })
            )
            .subscribe({
                next: () => {
                    console.log('User online status updated');
                },
                error: (error) => {
                    console.error('Failed to update user online status:', error);
                }
            });
    }

    /**
     * Stops the periodic online status update
     * Call this on logout
     */
    stopStatusUpdates(): void {
        if (this.statusUpdateSubscription) {
            this.statusUpdateSubscription.unsubscribe();
            this.statusUpdateSubscription = undefined;
        }
    }

    /**
     * Makes the API call to update online status
     */
    private updateOnlineStatus() {
        return this.http.get(`${this.baseUrl}/users/updateOnlineStatus`);
    }

    ngOnDestroy(): void {
        this.stopStatusUpdates();
    }
}
